import { useState, useRef, useCallback, useEffect } from "react";
import { Stage, Layer, Image as KonvaImage, Rect, Text, Transformer } from "react-konva";
import useImage from "use-image";

// ─── Color palette per label ────────────────────────────────────────────────
const LABEL_COLORS = {};
const PALETTE = [
  "#FF6B6B", "#FFD93D", "#6BCB77", "#4D96FF", "#FF922B",
  "#CC5DE8", "#20C997", "#F06595", "#74C0FC", "#A9E34B",
];
let colorIndex = 0;
function getLabelColor(label) {
  if (!LABEL_COLORS[label]) {
    LABEL_COLORS[label] = PALETTE[colorIndex % PALETTE.length];
    colorIndex++;
  }
  return LABEL_COLORS[label];
}

// ─── Parse XML string ────────────────────────────────────────────────────────
function parseAnnotationXml(xmlString) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(xmlString, "application/xml");
  const imageEl = doc.querySelector("image");
  if (!imageEl) return { imageName: null, imageWidth: 0, imageHeight: 0, boxes: [] };

  const imageName = imageEl.getAttribute("name");
  const imageWidth = parseFloat(imageEl.getAttribute("width"));
  const imageHeight = parseFloat(imageEl.getAttribute("height"));

  const boxes = Array.from(imageEl.querySelectorAll("box")).map((box, i) => ({
    id: `box-${i}`,
    label: box.getAttribute("label"),
    x: parseFloat(box.getAttribute("xtl")),
    y: parseFloat(box.getAttribute("ytl")),
    width: parseFloat(box.getAttribute("xbr")) - parseFloat(box.getAttribute("xtl")),
    height: parseFloat(box.getAttribute("ybr")) - parseFloat(box.getAttribute("ytl")),
  }));

  return { imageName, imageWidth, imageHeight, boxes };
}

// ─── Single annotation box (no transformer here) ────────────────────────────
// The Transformer lives in a separate top layer so its anchors are never
// occluded by — or confused with — other boxes in the annotation layer.
function AnnotationBox({ box, isSelected, onSelect, onChange, scale, rectRef }) {
  const color = getLabelColor(box.label);

  return (
    <>
      {/* Visible filled rect — draggable only when selected */}
      <Rect
        ref={rectRef}
        x={box.x}
        y={box.y}
        width={box.width}
        height={box.height}
        stroke={color}
        strokeWidth={isSelected ? 2.5 / scale : 2 / scale}
        fill={isSelected ? `${color}33` : `${color}22`}
        draggable={isSelected}
        // Non-selected fill must NOT intercept events — transformer anchor
        // mousedowns would otherwise land on the fill and drag a background
        // box instead of resizing the selected one.
        listening={isSelected}
        onClick={onSelect}
        onTap={onSelect}
        onDragEnd={(e) => onChange({ ...box, x: e.target.x(), y: e.target.y() })}
        onTransformEnd={() => {
          const node = rectRef.current;
          const sx = node.scaleX();
          const sy = node.scaleY();
          node.scaleX(1);
          node.scaleY(1);
          onChange({
            ...box,
            x: node.x(),
            y: node.y(),
            width: Math.max(5, node.width() * sx),
            height: Math.max(5, node.height() * sy),
          });
        }}
      />

      {/* Stroke-only hit rect for non-selected boxes.
          fill="transparent" + hitStrokeWidth means only the border region
          is clickable — the interior stays transparent to pointer events so
          transformer anchors of the selected box can never be swallowed. */}
      {!isSelected && (
        <Rect
          x={box.x}
          y={box.y}
          width={box.width}
          height={box.height}
          fill="transparent"
          stroke="transparent"
          strokeWidth={1}
          hitStrokeWidth={10 / scale}
          onClick={onSelect}
          onTap={onSelect}
        />
      )}

      {/* Label badge */}
      <Rect
        x={box.x}
        y={box.y - 20 / scale}
        width={box.label.length * 8 / scale + 8 / scale}
        height={18 / scale}
        fill={color}
        cornerRadius={3 / scale}
        listening={false}
      />
      <Text
        x={box.x + 4 / scale}
        y={box.y - 18 / scale}
        text={box.label}
        fontSize={11 / scale}
        fontFamily="'IBM Plex Mono', monospace"
        fontStyle="bold"
        fill="#fff"
        listening={false}
      />
    </>
  );
}

// ─── Transformer layer — sits above all annotation boxes ────────────────────
function SelectionTransformer({ selectedRectRef }) {
  const trRef = useRef();

  useEffect(() => {
    if (!trRef.current) return;
    if (selectedRectRef?.current) {
      trRef.current.nodes([selectedRectRef.current]);
    } else {
      trRef.current.nodes([]);
    }
    trRef.current.getLayer()?.batchDraw();
  }, [selectedRectRef]);

  return (
    <Transformer
      ref={trRef}
      rotateEnabled={false}
      boundBoxFunc={(oldBox, newBox) =>
        newBox.width < 5 || newBox.height < 5 ? oldBox : newBox
      }
    />
  );
}

// ─── Main Component ──────────────────────────────────────────────────────────
export default function XmlAnnotationViewer() {
  const [xmlText, setXmlText] = useState("");
  const [parsed, setParsed] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [imageUrl, setImageUrl] = useState(null);
  const [boxes, setBoxes] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  // fitScale: initial scale to fit image in container (never changes after load)
  // zoom: multiplicative zoom applied on top via mousewheel
  // stagePos: pan offset {x, y} updated during zoom to keep cursor-point stable
  const [fitScale, setFitScale] = useState(1);
  const [zoom, setZoom] = useState(1);
  const [stagePos, setStagePos] = useState({ x: 0, y: 0 });
  const [stageSize, setStageSize] = useState({ width: 800, height: 600 });
  const containerRef = useRef();
  const stageRef = useRef();
  // One ref per box — allows the transformer layer to attach to the exact
  // Konva node of the selected box without any layer-crossing confusion.
  const boxRefs = useRef({});

  const [konvaImage] = useImage(imageUrl);

  // Combined scale passed into boxes so strokes/labels stay visually constant
  const scale = fitScale * zoom;

  // Fit stage canvas to container width on load; reset zoom/pan
  useEffect(() => {
    if (!parsed || !containerRef.current) return;
    const containerWidth = containerRef.current.offsetWidth;
    const containerHeight = containerRef.current.offsetHeight || window.innerHeight * 0.8;
    const fs = Math.min(
      containerWidth / parsed.imageWidth,
      containerHeight / parsed.imageHeight,
      1
    );
    setFitScale(fs);
    setZoom(1);
    setStagePos({ x: 0, y: 0 });
    setStageSize({ width: containerWidth, height: containerHeight });
  }, [parsed]);

  const ZOOM_FACTOR = 1.08;
  const MIN_ZOOM = 0.2;
  const MAX_ZOOM = 10;

  const handleWheel = (e) => {
    e.evt.preventDefault();
    const stage = stageRef.current;
    if (!stage) return;

    const oldZoom = zoom;
    const direction = e.evt.deltaY < 0 ? 1 : -1;
    const newZoom = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, oldZoom * (direction > 0 ? ZOOM_FACTOR : 1 / ZOOM_FACTOR)));

    // Pointer position in screen space
    const pointer = stage.getPointerPosition();

    // Where the pointer sits in the content space (before new zoom)
    const contentX = (pointer.x - stagePos.x) / (fitScale * oldZoom);
    const contentY = (pointer.y - stagePos.y) / (fitScale * oldZoom);

    // New stage offset so that the content point under the cursor stays fixed
    const newX = pointer.x - contentX * fitScale * newZoom;
    const newY = pointer.y - contentY * fitScale * newZoom;

    setZoom(newZoom);
    setStagePos({ x: newX, y: newY });
  };

  const handleXmlUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = ev.target.result;
      setXmlText(text);
      const result = parseAnnotationXml(text);
      setParsed(result);
      setBoxes(result.boxes);
      setSelectedId(null);
    };
    reader.readAsText(file);
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImageFile(file);
    setImageUrl(URL.createObjectURL(file));
  };

  const handleBoxChange = useCallback((updated) => {
    setBoxes((prev) => prev.map((b) => (b.id === updated.id ? updated : b)));
  }, []);

  const isPanning = useRef(false);
  const lastPointerPos = useRef({ x: 0, y: 0 });
  const [isPanningCursor, setIsPanningCursor] = useState(false);

  const isBackground = (target) =>
    target === target.getStage() || target.getClassName?.() === "Image";

  const handleStageMouseDown = (e) => {
    if (isBackground(e.target)) {
      setSelectedId(null);
      isPanning.current = true;
      setIsPanningCursor(true);
      lastPointerPos.current = { x: e.evt.clientX, y: e.evt.clientY };
    }
  };

  const handleStageMouseMove = (e) => {
    if (!isPanning.current) return;
    const dx = e.evt.clientX - lastPointerPos.current.x;
    const dy = e.evt.clientY - lastPointerPos.current.y;
    lastPointerPos.current = { x: e.evt.clientX, y: e.evt.clientY };
    setStagePos((pos) => ({ x: pos.x + dx, y: pos.y + dy }));
  };

  const handleStageMouseUp = () => {
    isPanning.current = false;
    setIsPanningCursor(false);
  };

  const exportXml = () => {
    if (!parsed) return;
    const boxesXml = boxes
      .map(
        (b) =>
          `  <box label="${b.label}" xtl="${b.x.toFixed(2)}" ytl="${b.y.toFixed(2)}" xbr="${(b.x + b.width).toFixed(2)}" ybr="${(b.y + b.height).toFixed(2)}" occluded="0">\n    <attribute name="unbesch">true</attribute>\n  </box>`
      )
      .join("\n");
    const xml = `<?xml version="1.0" encoding="utf-8"?>\n<annotations>\n  <version>1.1</version>\n  <meta></meta>\n  <image id="0" name="${parsed.imageName}" width="${parsed.imageWidth}" height="${parsed.imageHeight}">\n${boxesXml}\n  </image>\n</annotations>`;
    const blob = new Blob([xml], { type: "application/xml" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "annotations_edited.xml";
    a.click();
  };

  // Unique labels for legend
  const uniqueLabels = [...new Set(boxes.map((b) => b.label))];

  return (
    <div style={styles.root}>
      <header style={styles.header}>
        <div style={styles.headerLeft}>
          <span style={styles.logo}>◈</span>
          <h1 style={styles.title}>HPM Annotator</h1>
        </div>
        <div style={styles.headerRight}>
          <span style={styles.count}>{boxes.length} boxes</span>
          {parsed && (
            <button style={styles.exportBtn} onClick={exportXml}>
              Export XML
            </button>
          )}
        </div>
      </header>

      <div style={styles.toolbar}>
        <label style={styles.uploadBtn}>
          <span>Load XML</span>
          <input type="file" accept=".xml" onChange={handleXmlUpload} style={{ display: "none" }} />
        </label>
        <label style={styles.uploadBtn}>
          <span>Load Image</span>
          <input type="file" accept="image/*" onChange={handleImageUpload} style={{ display: "none" }} />
        </label>
        {parsed && (
          <span style={styles.meta}>
            {parsed.imageWidth} × {parsed.imageHeight}px
          </span>
        )}
        {parsed && (
          <>
            <span style={{ ...styles.meta, marginLeft: 0 }}>
              {Math.round(zoom * fitScale * 100)}%
            </span>
            <button
              style={{ ...styles.exportBtn, background: "#1c1c1c", color: "#e8e0d4", border: "1px solid #333" }}
              onClick={() => { setZoom(1); setStagePos({ x: 0, y: 0 }); }}
            >
              Reset zoom
            </button>
          </>
        )}
      </div>

      <div style={styles.workspace}>
        <div ref={containerRef} style={styles.canvasContainer}>
          {!parsed ? (
            <div style={styles.placeholder}>
              <div style={styles.placeholderIcon}>◈</div>
              <p>Load an XML annotation file to begin</p>
            </div>
          ) : (
            <Stage
              ref={stageRef}
              width={stageSize.width}
              height={stageSize.height}
              scaleX={scale}
              scaleY={scale}
              x={stagePos.x}
              y={stagePos.y}
              onWheel={handleWheel}
              onMouseDown={handleStageMouseDown}
              onMouseMove={handleStageMouseMove}
              onMouseUp={handleStageMouseUp}
              onMouseLeave={handleStageMouseUp}
              style={{ background: "#0d0d0d", borderRadius: "4px", cursor: isPanningCursor ? "grabbing" : "grab" }}
            >
              {/* Layer 1 — image + all annotation boxes */}
              <Layer>
                {konvaImage && (
                  <KonvaImage
                    image={konvaImage}
                    width={parsed.imageWidth}
                    height={parsed.imageHeight}
                    onClick={() => setSelectedId(null)}
                    onTap={() => setSelectedId(null)}
                  />
                )}
                {boxes.map((box) => {
                  // Ensure a stable ref object exists for every box id
                  if (!boxRefs.current[box.id]) {
                    boxRefs.current[box.id] = { current: null };
                  }
                  return (
                    <AnnotationBox
                      key={box.id}
                      box={box}
                      scale={scale}
                      isSelected={box.id === selectedId}
                      onSelect={() => setSelectedId(box.id)}
                      onChange={handleBoxChange}
                      rectRef={boxRefs.current[box.id]}
                    />
                  );
                })}
              </Layer>

              {/* Layer 2 — transformer only, always on top so its anchors
                  are never occluded by boxes in the annotation layer */}
              <Layer>
                <SelectionTransformer
                  selectedRectRef={selectedId ? boxRefs.current[selectedId] : null}
                />
              </Layer>
            </Stage>
          )}
        </div>

        {parsed && (
          <aside style={styles.sidebar}>
            <h2 style={styles.sidebarTitle}>Labels</h2>
            <div style={styles.legend}>
              {uniqueLabels.map((label) => (
                <div key={label} style={styles.legendItem}>
                  <div style={{ ...styles.legendDot, background: getLabelColor(label) }} />
                  <span style={styles.legendLabel}>{label}</span>
                  <span style={styles.legendCount}>
                    {boxes.filter((b) => b.label === label).length}
                  </span>
                </div>
              ))}
            </div>

            {selectedId && (
              <div style={styles.inspector}>
                <h2 style={styles.sidebarTitle}>Inspector</h2>
                {(() => {
                  const b = boxes.find((x) => x.id === selectedId);
                  if (!b) return null;
                  return (
                    <div style={styles.inspectorGrid}>
                      {["x", "y", "width", "height"].map((field) => (
                        <label key={field} style={styles.inspectorField}>
                          <span style={styles.inspectorKey}>{field}</span>
                          <input
                            style={styles.inspectorInput}
                            type="number"
                            value={Math.round(b[field])}
                            onChange={(e) =>
                              handleBoxChange({ ...b, [field]: parseFloat(e.target.value) || 0 })
                            }
                          />
                        </label>
                      ))}
                      <label style={{ ...styles.inspectorField, gridColumn: "span 2" }}>
                        <span style={styles.inspectorKey}>label</span>
                        <input
                          style={styles.inspectorInput}
                          type="text"
                          value={b.label}
                          onChange={(e) => handleBoxChange({ ...b, label: e.target.value })}
                        />
                      </label>
                    </div>
                  );
                })()}
              </div>
            )}

            <div style={styles.hint}>
              <p>Click a box to select</p>
              <p>Drag to move</p>
              <p>Handles to resize</p>
            </div>
          </aside>
        )}
      </div>
    </div>
  );
}

// ─── Styles ──────────────────────────────────────────────────────────────────
const styles = {
  root: {
    fontFamily: "'IBM Plex Mono', 'Courier New', monospace",
    background: "#0a0a0a",
    color: "#e8e0d4",
    minHeight: "100vh",
    display: "flex",
    flexDirection: "column",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "14px 24px",
    borderBottom: "1px solid #2a2a2a",
    background: "#111",
  },
  headerLeft: { display: "flex", alignItems: "center", gap: "12px" },
  logo: { fontSize: "22px", color: "#c9a84c" },
  title: { margin: 0, fontSize: "16px", fontWeight: 700, letterSpacing: "0.08em", color: "#e8e0d4" },
  headerRight: { display: "flex", alignItems: "center", gap: "14px" },
  count: { fontSize: "12px", color: "#666", letterSpacing: "0.05em" },
  exportBtn: {
    background: "#c9a84c",
    color: "#0a0a0a",
    border: "none",
    borderRadius: "3px",
    padding: "6px 14px",
    fontSize: "12px",
    fontFamily: "inherit",
    fontWeight: 700,
    cursor: "pointer",
    letterSpacing: "0.05em",
  },
  toolbar: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    padding: "10px 24px",
    borderBottom: "1px solid #1e1e1e",
    background: "#0e0e0e",
  },
  uploadBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    padding: "6px 14px",
    background: "#1c1c1c",
    border: "1px solid #333",
    borderRadius: "3px",
    fontSize: "12px",
    cursor: "pointer",
    color: "#e8e0d4",
    letterSpacing: "0.04em",
    transition: "border-color 0.15s",
  },
  meta: { fontSize: "11px", color: "#555", marginLeft: "auto" },
  workspace: { display: "flex", flex: 1, gap: 0 },
  canvasContainer: {
    flex: 1,
    padding: "20px",
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "center",
    overflow: "hidden",
    background: "#0a0a0a",
    minHeight: "600px",
  },
  placeholder: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    height: "400px",
    color: "#444",
    gap: "16px",
    fontSize: "13px",
  },
  placeholderIcon: { fontSize: "48px", color: "#2a2a2a" },
  sidebar: {
    width: "220px",
    minWidth: "220px",
    background: "#0e0e0e",
    borderLeft: "1px solid #1e1e1e",
    padding: "20px 16px",
    display: "flex",
    flexDirection: "column",
    gap: "20px",
    overflowY: "auto",
  },
  sidebarTitle: {
    margin: "0 0 10px 0",
    fontSize: "10px",
    fontWeight: 700,
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    color: "#c9a84c",
  },
  legend: { display: "flex", flexDirection: "column", gap: "6px" },
  legendItem: { display: "flex", alignItems: "center", gap: "8px" },
  legendDot: { width: "10px", height: "10px", borderRadius: "2px", flexShrink: 0 },
  legendLabel: { flex: 1, fontSize: "12px", color: "#ccc" },
  legendCount: { fontSize: "11px", color: "#555" },
  inspector: {},
  inspectorGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "8px",
  },
  inspectorField: { display: "flex", flexDirection: "column", gap: "4px" },
  inspectorKey: { fontSize: "10px", color: "#666", letterSpacing: "0.08em", textTransform: "uppercase" },
  inspectorInput: {
    background: "#1a1a1a",
    border: "1px solid #333",
    borderRadius: "3px",
    color: "#e8e0d4",
    padding: "4px 6px",
    fontSize: "12px",
    fontFamily: "inherit",
    width: "100%",
    boxSizing: "border-box",
  },
  hint: { marginTop: "auto", display: "flex", flexDirection: "column", gap: "4px" },
};
